import { createGlobalStyle } from "styled-components";
import { designTokens } from "./theme/designTokens";

export const GlobalStyle = createGlobalStyle`
  /* ========================================
     BASE STYLES
     ======================================== */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Text Selection Styling */
  ::selection {
    background-color: ${designTokens.colors.accent.light};
    color: ${designTokens.colors.neutral.darker};
    text-shadow: none;
  }

  /* ========================================
     TYPOGRAPHY & BODY
     ======================================== */
  body {
    font-family: ${designTokens.typography.fontFamily.primary}, ${designTokens.typography.fontFamily.fallback};
    font-weight: ${designTokens.typography.fontSize.body.normal.weight};
    font-size: ${designTokens.typography.fontSize.body.normal.size};
    line-height: ${designTokens.typography.fontSize.body.normal.lineHeight};
    color: ${designTokens.colors.neutral.dark};
    background-color: ${designTokens.colors.neutral.light};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Headings - Professional Typography Hierarchy */
  h1 {
    font-size: ${designTokens.typography.fontSize.h1.desktop};
    font-weight: ${designTokens.typography.fontSize.h1.weight};
    line-height: ${designTokens.typography.fontSize.h1.lineHeight};
    color: ${designTokens.colors.neutral.darker};
    margin: ${designTokens.spacing.lg} 0;
    
    @media (max-width: ${designTokens.breakpoints.tablet}) {
      font-size: ${designTokens.typography.fontSize.h1.tablet};
    }
    
    @media (max-width: ${designTokens.breakpoints.mobile}) {
      font-size: ${designTokens.typography.fontSize.h1.mobile};
    }
  }

  h2 {
    font-size: ${designTokens.typography.fontSize.h2.desktop};
    font-weight: ${designTokens.typography.fontSize.h2.weight};
    line-height: ${designTokens.typography.fontSize.h2.lineHeight};
    color: ${designTokens.colors.neutral.darker};
    margin: ${designTokens.spacing.lg} 0 ${designTokens.spacing.md} 0;
    
    @media (max-width: ${designTokens.breakpoints.tablet}) {
      font-size: ${designTokens.typography.fontSize.h2.tablet};
    }
    
    @media (max-width: ${designTokens.breakpoints.mobile}) {
      font-size: ${designTokens.typography.fontSize.h2.mobile};
    }
  }

  h3 {
    font-size: ${designTokens.typography.fontSize.h3.desktop};
    font-weight: ${designTokens.typography.fontSize.h3.weight};
    line-height: ${designTokens.typography.fontSize.h3.lineHeight};
    color: ${designTokens.colors.neutral.darker};
    margin: ${designTokens.spacing.md} 0 ${designTokens.spacing.sm} 0;
    
    @media (max-width: ${designTokens.breakpoints.tablet}) {
      font-size: ${designTokens.typography.fontSize.h3.tablet};
    }
    
    @media (max-width: ${designTokens.breakpoints.mobile}) {
      font-size: ${designTokens.typography.fontSize.h3.mobile};
    }
  }

  h4, h5, h6 {
    font-weight: 600;
    line-height: 1.4;
    color: ${designTokens.colors.neutral.darker};
    margin: ${designTokens.spacing.md} 0 ${designTokens.spacing.sm} 0;
  }

  /* Links */
  a {
    color: ${designTokens.colors.primary.main};
    text-decoration: none;
    background-color: transparent;
    transition: color ${designTokens.transitions.fast};

    &:hover {
      color: ${designTokens.colors.primary.dark};
    }
  }

  /* Properties Link */
  a[href="/properties"],
  .siteMenu a[href="/properties"] {
    cursor: pointer;
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Lists */
  ul, ol {
    list-style: none;
  }

  p {
    margin: 0 0 ${designTokens.spacing.md} 0;
    line-height: ${designTokens.typography.fontSize.body.normal.lineHeight};
  }

  /* ========================================
     FORM ELEMENTS
     ======================================== */
  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
  }

  button {
    font-family: inherit;
    cursor: pointer;
    transition: all ${designTokens.transitions.normal};
  }

  /* ========================================
     UTILITY CLASSES
     ======================================== */
  .m-0 {
    margin: 0 !important;
  }

  .animated {
    animation-duration: 1s;
    animation-fill-mode: both;
  }

  .fadeIn {
    animation-name: fadeIn;
  }

  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }

  /* Dropdown Menu */
  .dropdown-item {
    display: block;
    font-size: ${designTokens.typography.fontSize.body.normal.size};
    font-weight: 400;
    color: ${designTokens.colors.neutral.dark};
    text-decoration: none;
    padding: ${designTokens.spacing.sm} ${designTokens.spacing.md};
    margin: ${designTokens.spacing.sm} 0;
    border-radius: ${designTokens.borderRadius.medium};
    transition: color ${designTokens.transitions.fast}, background-color ${designTokens.transitions.fast};

    &:hover {
      color: ${designTokens.colors.neutral.white};
      background-color: ${designTokens.colors.primary.main};
    }
  }

  .dropdown-toggle {
    margin: ${designTokens.spacing.sm} 0;
  }

  .dropdown-menu {
    padding: ${designTokens.spacing.md};
    background: ${designTokens.colors.neutral.white};
    border-radius: ${designTokens.borderRadius.large};
    box-shadow: ${designTokens.shadows.lg};
  }

  .dropdown-menu li {
    position: relative;
  }

  .dropdown:hover > .dropdown-menu {
    display: block;
  }

  /* Spacing Utilities */
  .margin-top--sm { margin-top: ${designTokens.spacing.lg}; }
  .margin-top--md { margin-top: ${designTokens.spacing.xl}; }
  .margin-top--lg { margin-top: ${designTokens.spacing.xxl}; }
  .margin-top--huge { margin-top: ${designTokens.spacing.xxxl}; }

  .margin-bottom--sm { margin-bottom: ${designTokens.spacing.lg}; }
  .margin-bottom--md { margin-bottom: ${designTokens.spacing.xl}; }
  .margin-bottom--lg { margin-bottom: ${designTokens.spacing.xxl}; }
  .margin-bottom--huge { margin-bottom: ${designTokens.spacing.xxxl}; }

  .margin-sm { margin: ${designTokens.spacing.lg}; }
  .margin-md { margin: ${designTokens.spacing.xl}; }
  .margin-lg { margin: ${designTokens.spacing.xxl}; }
  .margin-huge { margin: ${designTokens.spacing.xxxl}; }

  /* Padding Utilities */
  .padding-top--sm { padding-top: ${designTokens.spacing.lg}; }
  .padding-top--md { padding-top: ${designTokens.spacing.xl}; }
  .padding-top--lg { padding-top: ${designTokens.spacing.xxl}; }
  .padding-top--huge { padding-top: ${designTokens.spacing.xxxl}; }

  .padding-bottom--sm { padding-bottom: ${designTokens.spacing.lg}; }
  .padding-bottom--md { padding-bottom: ${designTokens.spacing.xl}; }
  .padding-bottom--lg { padding-bottom: ${designTokens.spacing.xxl}; }
  .padding-bottom--huge { padding-bottom: ${designTokens.spacing.xxxl}; }

  .padding-sm { padding: ${designTokens.spacing.lg}; }
  .padding-md { padding: ${designTokens.spacing.xl}; }
  .padding-lg { padding: ${designTokens.spacing.xxl}; }
  .padding-huge { padding: ${designTokens.spacing.xxxl}; }

  /* Empty State */
  .empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    margin-top: ${designTokens.spacing.lg};
    background: ${designTokens.colors.neutral.white};
    box-shadow: ${designTokens.shadows.sm};
    border-radius: ${designTokens.borderRadius.large};
    padding: ${designTokens.spacing.xl};
    transition: box-shadow ${designTokens.transitions.normal};
    flex: 1 100%;
    margin: ${designTokens.spacing.md};

    @media (max-width: ${designTokens.breakpoints.tablet}) {
      width: 100%;
      margin: ${designTokens.spacing.md} ${designTokens.spacing.sm};
    }
  }

  .empty-title {
    font-size: 32px;
    font-weight: 700;
    color: ${designTokens.colors.neutral.darker};
    text-align: center;
  }

  .empty-message {
    font-size: ${designTokens.typography.fontSize.body.normal.size};
    line-height: 1.6;
    padding: ${designTokens.spacing.lg} ${designTokens.spacing.md};
    text-align: center;
    color: ${designTokens.colors.neutral.medium};
  }

  @media (max-width: ${designTokens.breakpoints.tablet}) {
    .empty-title {
      font-size: 24px;
    }

    .empty-message {
      font-size: 14px;
    }
  }
`;
